import { useState, useMemo, useEffect } from "react";
import { Eye, Pencil, Trash2, ChevronDown, Folder } from "lucide-react";
import Pagination from "@/components/dashboard/pagination";
import { IDocumento, IDocumentFilters } from "@/types/documento";
import InputSearch from "../ui/input-search";

interface DocumentosTableProps {
  documents: IDocumento[];
  filters: IDocumentFilters;
  onViewDocument: (doc: IDocumento) => void;
  onEditDocument: (doc: IDocumento) => void;
  onDeleteDocument: (doc: IDocumento) => void;
}

export function DocumentosTable({
  documents,
  filters,
  onViewDocument,
  onEditDocument,
  onDeleteDocument,
}: DocumentosTableProps) {
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 10;

  // Combined filtering logic
  const filteredDocs = useMemo(() => {
    let result = [...documents];

    // 1. Search filter (local state)
    const term = searchTerm.toLowerCase().trim();
    if (term) {
      result = result.filter(
        (doc) =>
          doc.fileName.toLowerCase().includes(term) ||
          doc.responsible.name.toLowerCase().includes(term)
      );
    }

    // 2. Document type filter
    if (filters.documentType.length > 0) {
      result = result.filter((doc) =>
        filters.documentType.includes(doc.documentType)
      );
    }

    // 3. Stage filter
    if (filters.stage.length > 0) {
      result = result.filter((doc) => filters.stage.includes(doc.stage));
    }

    // 4. Time range filter
    if (filters.timeRange !== "todos") {
      const now = new Date();

      switch (filters.timeRange) {
        case "90-dias": {
          const ninetyDaysFromNow = new Date(now);
          ninetyDaysFromNow.setDate(now.getDate() + 90);
          result = result.filter((doc) => {
            const docDate = new Date(doc.date);
            return docDate >= now && docDate <= ninetyDaysFromNow;
          });
          break;
        }
        case "30-dias": {
          const thirtyDaysFromNow = new Date(now);
          thirtyDaysFromNow.setDate(now.getDate() + 30);
          result = result.filter((doc) => {
            const docDate = new Date(doc.date);
            return docDate >= now && docDate <= thirtyDaysFromNow;
          });
          break;
        }
        case "este-mes": {
          const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
          const endOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0);
          result = result.filter((doc) => {
            const docDate = new Date(doc.date);
            return docDate >= startOfMonth && docDate <= endOfMonth;
          });
          break;
        }
      }
    }

    return result;
  }, [documents, searchTerm, filters]);

  const totalPages = Math.max(1, Math.ceil(filteredDocs.length / pageSize));

  const paginatedDocs = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return filteredDocs.slice(start, start + pageSize);
  }, [filteredDocs, currentPage]);

  // Reset page when search changes
  useEffect(() => {
    setCurrentPage(1);
  }, [filteredDocs.length]);

  return (
    <div className="bg-[#15203C] rounded-[20px] p-[30px]">
      {/* Header with search and filter */}
      <div className="flex items-center justify-between mb-[30px]">
        <h2 className="text-[18px] font-bold text-white font-['Plus_Jakarta_Sans']">
          Documentos recentes
        </h2>

        <div className="flex items-center gap-[10px]">
          <InputSearch 
            positionIcon="end"
            value={searchTerm}
            onChange={setSearchTerm}
          />

          {/* Ver mais button */}
          <button className="border border-[#5D5D5D] rounded-[10px] px-[20px] py-[8px] h-[36px] flex items-center gap-[4px] hover:bg-white/5 transition-colors">
            <p className="text-[12px] text-[#F6F6F6] font-['Plus_Jakarta_Sans']">Ver mais</p>
            <ChevronDown className="size-[16px] text-white rotate-90" />
          </button>
        </div>
      </div>

      {/* Table Header */}
      <div className="bg-[#0C1427] rounded-[8px] px-[20px] py-[10px] mb-[1px] grid grid-cols-[2fr_1.5fr_1fr_1fr_1.2fr_1fr] gap-[59px]">
        <p className="text-[12px] font-bold text-[#F6F6F6] font-['Plus_Jakarta_Sans']">Nome do Arquivo</p>
        <p className="text-[12px] font-bold text-[#F6F6F6] font-['Plus_Jakarta_Sans']">Responsável</p>
        <p className="text-[12px] font-bold text-[#F6F6F6] font-['Plus_Jakarta_Sans']">Data</p>
        <p className="text-[12px] font-bold text-[#F6F6F6] font-['Plus_Jakarta_Sans']">Tipo</p>
        <p className="text-[12px] font-bold text-[#F6F6F6] font-['Plus_Jakarta_Sans']">Etapa</p>
        <p className="text-[12px] font-bold text-[#F6F6F6] font-['Plus_Jakarta_Sans']">Ações</p>
      </div>

      {/* Table Rows */}
      <div>
        {paginatedDocs.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-[#888888] text-sm font-['Plus_Jakarta_Sans']">Nenhum documento encontrado</p>
            {searchTerm && (
              <button
                onClick={() => setSearchTerm("")}
                className="text-[#7846FF] text-sm mt-2 hover:underline font-['Plus_Jakarta_Sans']"
              >
                Limpar busca
              </button>
            )}
          </div>
        ) : (
          paginatedDocs.map((doc, index) => (
            <div key={doc.id}>
              <div className="px-[20px] py-[10px] grid grid-cols-[2fr_1.5fr_1fr_1fr_1.2fr_1fr] gap-[59px] items-center">
                {/* File name with icon */}
                <div className="flex items-center gap-[10px]">
                  <Folder className="size-[24px] text-white flex-shrink-0" />
                  <p className="text-[14px] text-[#F6F6F6] font-['Plus_Jakarta_Sans']">{doc.fileName}</p>
                </div>

                {/* Responsible with avatar */}
                <div className="flex items-center gap-[10px]">
                  <div className="size-[22px] rounded-full bg-gray-600 overflow-hidden flex-shrink-0">
                    {doc.responsible.avatar ? (
                      <img
                        src={doc.responsible.avatar}
                        alt={doc.responsible.name}
                        className="size-full object-cover"
                      />
                    ) : (
                      <div className="size-full bg-gray-600" />
                    )}
                  </div>
                  <p className="text-[14px] font-semibold text-[#F6F6F6] font-['Plus_Jakarta_Sans']">
                    {doc.responsible.name}
                  </p>
                </div>

                {/* Date */}
                <p className="text-[14px] text-[#F6F6F6] font-['Plus_Jakarta_Sans']">{doc.date}</p>

                {/* File type */}
                <p className="text-[14px] text-[#F6F6F6] font-['Plus_Jakarta_Sans']">{doc.fileType}</p>

                {/* Stage */}
                <p className="text-[14px] font-semibold text-[#F6F6F6] font-['Plus_Jakarta_Sans']">{doc.stage}</p>

                {/* Actions */}
                <div className="flex items-center gap-[10px]">
                  <button
                    onClick={() => onViewDocument(doc)}
                    className="hover:opacity-70 transition-opacity"
                    aria-label="Visualizar documento"
                  >
                    <Eye className="size-[24px] text-white" />
                  </button>
                  <button
                    onClick={() => onEditDocument(doc)}
                    className="hover:opacity-70 transition-opacity"
                    aria-label="Editar documento"
                  >
                    <Pencil className="size-[24px] text-white" />
                  </button>
                  <button
                    onClick={() => onDeleteDocument(doc)}
                    className="hover:opacity-70 transition-opacity"
                    aria-label="Excluir documento"
                  >
                    <Trash2 className="size-[24px] text-[#DC4426]" />
                  </button>
                </div>
              </div>

              {/* Divider line */}
              {index < paginatedDocs.length - 1 && (
                <div className="h-0 border-t border-gray-800/60" />
              )}
            </div>
          ))
        )}
      </div>

      {/* Footer with count and pagination */}
      {paginatedDocs.length > 0 && (
        <div className="flex items-center justify-between mt-[30px]">
          <p className="text-[14px] text-[#888888] font-['Plus_Jakarta_Sans']">
            Exibindo {paginatedDocs.length} de {filteredDocs.length} documentos
          </p>
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
          />
        </div>
      )}
    </div>
  );
}
